import { formatMoney, formatSignedMoney, TenantCurrency } from 'src/common/currency';
import { ChartView, NetWorthBasis, ProjectionSummary } from './types';

export type KpiTone = 'good' | 'warn' | 'bad';
export type Kpi = { label: string; value: string; tone?: KpiTone };

export const resilienceTone = (pct: number): KpiTone => {
  if (pct >= 80) return 'good';
  if (pct >= 50) return 'warn';
  return 'bad';
};

// Projections made before the cut-off could be set ran to 105 and do not say so.
const DEFAULT_HORIZON_AGE = 105;

export const horizonAge = (age: number | null | undefined): number => age ?? DEFAULT_HORIZON_AGE;

const legacyLabel = (view: ChartView, basis: NetWorthBasis, age: number): string => {
  const money = view === 'real' ? "today's money" : 'future money';
  return basis === 'liquid'
    ? `Spendable assets at ${age} (${money})`
    : `Total estate at ${age} (${money})`;
};

export const buildKpis = (
  summary: ProjectionSummary,
  view: ChartView,
  currency: TenantCurrency,
  resiliencePct?: number | null,
  basis: NetWorthBasis = 'total',
): Kpi[] => {
  const total = view === 'real' ? summary.legacy_real : summary.legacy_nominal;
  const liquid = view === 'real' ? summary.liquid_legacy_real : summary.liquid_legacy_nominal;
  const legacy = basis === 'liquid' ? (liquid ?? total) : total;
  return [
    ...(resiliencePct != null
      ? [
          {
            label: 'Resilience score',
            value: `${Math.round(resiliencePct)}%`,
            tone: resilienceTone(resiliencePct),
          },
        ]
      : []),
    {
      label: legacyLabel(view, basis, horizonAge(summary.horizon_age)),
      value: legacy != null ? formatSignedMoney(legacy, currency) : '—',
    },
    {
      label: 'Peak net worth',
      value:
        summary.peak_net_worth_value != null
          ? `${formatSignedMoney(summary.peak_net_worth_value, currency)}${
              summary.peak_net_worth_age != null
                ? ` at ${Math.round(summary.peak_net_worth_age)}`
                : ''
            }`
          : '—',
    },
    {
      label: 'First shortfall age',
      value:
        summary.first_shortfall_age != null
          ? String(Math.floor(summary.first_shortfall_age))
          : `None before ${horizonAge(summary.horizon_age)}`,
    },
    {
      label: 'Income, first year',
      value:
        summary.total_income_first_year != null
          ? formatMoney(summary.total_income_first_year, currency)
          : '—',
    },
    {
      label: 'Spending, first year',
      value:
        summary.total_expenditure_first_year != null
          ? formatMoney(summary.total_expenditure_first_year, currency)
          : '—',
    },
    ...(summary.estate_value_at_le != null
      ? [
          {
            label: 'Estate at life expectancy',
            value: formatMoney(Math.round(summary.estate_value_at_le), currency),
          },
        ]
      : []),
    ...(summary.iht_due_at_le != null
      ? [
          {
            label: 'IHT at life expectancy',
            value: formatMoney(Math.round(summary.iht_due_at_le), currency),
            tone: summary.iht_due_at_le > 0 ? ('warn' as const) : ('good' as const),
          },
        ]
      : []),
  ];
};
